// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import {
  CollateralAgreement,
  CollateralAgreementChangeRequest,
  CollateralAgreementRequest,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Collateral';
import { UserService } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import { ContractId } from '@daml/types';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import useCollateralState from '../../../hooks/queries/collateral/collateral/useCollateralState';
import { useCollateralUserService } from '../../../hooks/queries/collateral/onboarding/useUserService';
import {
  ERR_ACC_AGR_REQ,
  ERR_ACC_CHANGE_REQ,
  ERR_CAN_AGR_REQ,
  ERR_CAN_CHANGE_REQ,
  ERR_REJ_AGR_REQ,
  ERR_REJ_CHANGE_REQ,
  ERR_TERMINATE_COL_AGREEMENT,
  SUC_ACC_AGR_REQ,
  SUC_ACC_CHANGE_REQ,
  SUC_CAN_AGR_REQ,
  SUC_CAN_CHANGE_REQ,
  SUC_REJ_AGR_REQ,
  SUC_REJ_CHANGE_REQ,
  SUC_TERMINATE_COL_AGREEMENT,
} from '../../../utils/strings';
import { CollateralAgreementRow, GenericAction } from '../collaterals/types';

type Action = GenericAction<CollateralAgreementRow>;

interface FilterCollateralStateArgs {
  partyA: string;
  partyB: string;
  operator: string;
  id: string;
}

export const useCreateAgreementActions = () => {
  const party = useModuleParty();
  const userService = useCollateralUserService();
  const collateralStates = useCollateralState();

  const { showBoundary } = useErrorBoundary<Error>();
  const [dialogName, setDialogName] = React.useState('');
  const [onTerminate, setOnTerminate] = React.useState<(() => Promise<unknown>) | undefined>();

  const { submit: terminate } = useExerciseSubmission(
    UserService.UserService_TerminateCollateralAgreement,
    SUC_TERMINATE_COL_AGREEMENT,
    ERR_TERMINATE_COL_AGREEMENT,
  );

  const { submit: acceptCollateralAgreementRequest } = useExerciseSubmission(
    UserService.UserService_AcceptCollateralAgreementRequest,
    SUC_ACC_AGR_REQ,
    ERR_ACC_AGR_REQ,
  );

  const { submit: rejectCollateralAgreementRequest } = useExerciseSubmission(
    UserService.UserService_RejectCollateralAgreementRequest,
    SUC_REJ_AGR_REQ,
    ERR_REJ_AGR_REQ,
  );

  const { submit: cancelCollateralAgreementRequest } = useExerciseSubmission(
    UserService.UserService_CancelCollateralAgreementRequest,
    SUC_CAN_AGR_REQ,
    ERR_CAN_AGR_REQ,
  );

  const { submit: acceptCollateralAgreementChangeRequest } = useExerciseSubmission(
    UserService.UserService_AcceptCollateralAgreementChangeRequest,
    SUC_ACC_CHANGE_REQ,
    ERR_ACC_CHANGE_REQ,
  );

  const { submit: cancelCollateralAgreementChangeRequest } = useExerciseSubmission(
    UserService.UserService_CancelCollateralAgreementChangeRequest,
    SUC_CAN_CHANGE_REQ,
    ERR_CAN_CHANGE_REQ,
  );

  const { submit: rejectCollateralAgreementChangeRequest } = useExerciseSubmission(
    UserService.UserService_RejectCollateralAgreementChangeRequest,
    SUC_REJ_CHANGE_REQ,
    ERR_REJ_CHANGE_REQ,
  );

  const acceptChangeRequest = React.useCallback(
    (row: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_ACC_CHANGE_REQ, { cause: 'User service is not available.' })),
        );

      return acceptCollateralAgreementChangeRequest({
        cid: userService.data.contractId,
        argument: {
          cid: row.contractId as ContractId<CollateralAgreementChangeRequest>,
          payload: {
            actor: party,
          },
        },
      });
    },
    [acceptCollateralAgreementChangeRequest, party, showBoundary, userService.data],
  );
  const cancelChangeRequest = React.useCallback(
    (row: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_CAN_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return cancelCollateralAgreementChangeRequest({
        cid: userService.data.contractId,
        argument: {
          cid: row.contractId as ContractId<CollateralAgreementChangeRequest>,
          payload: { actor: party },
        },
      });
    },
    [cancelCollateralAgreementChangeRequest, party, showBoundary, userService.data],
  );
  const rejectChangeRequest = React.useCallback(
    (r: CollateralAgreementRow, reason: string) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_REJ_CHANGE_REQ, { cause: 'User service is not available.' })),
        );

      return rejectCollateralAgreementChangeRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementChangeRequest>,
          payload: { reason },
        },
      });
    },
    [userService.data, rejectCollateralAgreementChangeRequest, showBoundary],
  );
  const terminateCollateralAgreement = React.useCallback(
    (row: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_ACC_AGR_REQ, { cause: 'User service is not available.' })),
        );
      const filterCollateralStates = (args: FilterCollateralStateArgs) => {
        return (collateralStates.data || []).filter(
          (cs) =>
            cs.payload.partyA === args.partyA &&
            cs.payload.partyB === args.partyB &&
            cs.payload.id === args.id &&
            cs.payload.operator === args.operator,
        );
      };
      const [collateralStateCid] = filterCollateralStates({
        operator: row.payload.operator,
        id: row.payload.id,
        partyA: row.payload.requestor,
        partyB: row.payload.counterparty,
      });

      return terminate({
        cid: userService.data.contractId,
        argument: {
          cid: row.contractId as ContractId<CollateralAgreement>,
          payload: {
            actor: party,
            collateralStateCid: collateralStateCid.contractId,
          },
        },
      });
    },
    [party, terminate, showBoundary, userService.data, collateralStates.data],
  );
  const [onReject, setOnReject] = React.useState<
    ((reason: string) => Promise<unknown>) | undefined
  >();
  const acceptAgreement = React.useCallback(
    (r: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_ACC_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return acceptCollateralAgreementRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementRequest>,
          payload: {},
        },
      });
    },
    [userService.data, acceptCollateralAgreementRequest, showBoundary],
  );

  const rejectAgreement = React.useCallback(
    (r: CollateralAgreementRow, reason: string) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_REJ_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return rejectCollateralAgreementRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementRequest>,
          payload: { reason },
        },
      });
    },
    [userService.data, rejectCollateralAgreementRequest, showBoundary],
  );

  const cancelAgreement = React.useCallback(
    (r: CollateralAgreementRow) => {
      if (!userService.data)
        return Promise.resolve(
          showBoundary(new Error(ERR_CAN_AGR_REQ, { cause: 'User service is not available.' })),
        );

      return cancelCollateralAgreementRequest({
        cid: userService.data.contractId,
        argument: {
          cid: r.contractId as ContractId<CollateralAgreementRequest>,
          payload: {},
        },
      });
    },
    [userService.data, cancelCollateralAgreementRequest, showBoundary],
  );

  const isUser = !!userService.data;

  const createCollateralActions = (r: CollateralAgreementRow) => {
    let actions: Action[] = [];
    if (r.state === 'Change Requested') {
      if (r.payload.requestor === party) {
        actions = actions.concat([
          {
            name: 'Cancel',
            action: cancelChangeRequest,
            buttonProps: { variant: 'outlined', color: 'error' },
          },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
      if (r.payload.counterparty === party) {
        actions = actions.concat([
          { name: 'Accept', action: acceptChangeRequest },
          {
            name: 'Reject',
            buttonProps: {
              variant: 'outlined',
              color: 'error',
            },
            action: () => {
              setOnReject(() => (reason: string) => rejectChangeRequest(r, reason));
              return Promise.resolve();
            },
          },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
    }
    if (r.state === 'Accepted') {
      actions = actions.concat([
        {
          inMoreMenu: true,
          name: 'View details',
          action: () => {
            setDialogName('details');
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        },
        {
          inMoreMenu: true,
          name: 'Instruct collateral',
          action: () => {
            setDialogName('instructCollateral');
            setOnTerminate(() => () => new Promise(() => {}));
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        },
        {
          inMoreMenu: true,
          name: 'Settlement history',
          action: () => {
            setDialogName('history');
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        },
        {
          inMoreMenu: true,
          name: 'Modify agreement',
          action: () => {
            setDialogName('modify');
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined' },
        },
        {
          inMoreMenu: true,
          name: 'Terminate agreement',
          action: () => {
            setDialogName('terminate');
            setOnTerminate(
              () => () => terminateCollateralAgreement(r).then(() => setDialogName('')),
            );
            return Promise.resolve();
          },
          buttonProps: { variant: 'outlined', color: 'error' },
          color: 'error',
        },
      ]);
    }
    if (isUser && r.state === 'Pending') {
      if (r.payload.counterparty === party) {
        actions = actions.concat([
          { name: 'Accept', action: acceptAgreement },
          {
            name: 'Reject',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setOnReject(() => (reason: string) => rejectAgreement(r, reason));
              return Promise.resolve();
            },
          },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
      if (r.payload.requestor === party) {
        actions = actions.concat([
          { name: 'Cancel', action: cancelAgreement, buttonProps: { variant: 'outlined' } },
          {
            inMoreMenu: true,
            name: 'View details',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('details');
              return Promise.resolve();
            },
          },
        ]);
      }
    }

    return actions;
  };
  return {
    createCollateralActions,
    onReject,
    setOnReject,
    onTerminate,
    setOnTerminate,
    dialogName,
    setDialogName,
  };
};
